import { STAFF_TENANT_CHANGE_EVENT, roleCanAccess } from "@l30/shared";
import { useRouterState } from "@tanstack/react-router";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import { useCurrentRole } from "../../api/auth.js";

import { CaptureModal } from "./capture-modal.js";

interface CaptureContextValue {
  open: (subjectId?: string) => void;
  close: () => void;
}

const CaptureContext = createContext<CaptureContextValue | null>(null);

export function useCapture(): CaptureContextValue {
  const ctx = useContext(CaptureContext);
  if (!ctx) {
    throw new Error("useCapture must be used within a CaptureProvider");
  }
  return ctx;
}

export function useOptionalCapture(): CaptureContextValue | null {
  return useContext(CaptureContext);
}

export function CaptureProvider({ children }: { children: ReactNode }) {
  const role = useCurrentRole();
  const canCapture = roleCanAccess("ideation", role);
  const { location } = useRouterState();
  const [open, setOpen] = useState(false);
  const [subjectId, setSubjectId] = useState<string | null>(null);

  const routeSubject = useMemo(() => {
    const match = location.pathname.match(/^\/subject\/([^/]+)/);
    return match?.[1] ?? null;
  }, [location.pathname]);

  const closeCapture = useCallback(() => {
    setOpen(false);
    setSubjectId(null);
  }, []);

  useEffect(() => {
    const reset = () => closeCapture();
    window.addEventListener(STAFF_TENANT_CHANGE_EVENT, reset);
    return () => window.removeEventListener(STAFF_TENANT_CHANGE_EVENT, reset);
  }, [closeCapture]);

  const openCapture = useCallback(
    (id?: string) => {
      if (!canCapture) return;
      setSubjectId(id ?? routeSubject);
      setOpen(true);
    },
    [canCapture, routeSubject],
  );

  const value = useMemo(
    () => ({ open: openCapture, close: closeCapture }),
    [openCapture, closeCapture],
  );

  return (
    <CaptureContext.Provider value={value}>
      {children}
      {canCapture ? (
        <CaptureModal
          key={`${String(open)}:${subjectId ?? "none"}`}
          open={open}
          subjectId={subjectId}
          onClose={closeCapture}
        />
      ) : null}
    </CaptureContext.Provider>
  );
}
